import React from 'react';
import { Icon } from '@iconify/react';

type AlertVariant = 'warning' | 'success' | 'danger' | 'default';

interface AlertProps {
  children: React.ReactNode;
  variant?: AlertVariant;
  className?: string;
  showIcon?: boolean;
}

const variantStyles: Record<
  AlertVariant,
  {
    container: string;
    text: string;
    icon: string;
  }
> = {
  warning: {
    container: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950',
    text: 'text-amber-600 dark:text-amber-300',
    icon: 'tabler:alert-triangle',
  },
  success: {
    container: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950',
    text: 'text-green-600 dark:text-green-300',
    icon: 'tabler:circle-check',
  },
  danger: {
    container: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
    text: 'text-red-600 dark:text-red-300',
    icon: 'tabler:alert-circle',
  },
  default: {
    container: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950',
    text: 'text-blue-600 dark:text-blue-300',
    icon: 'tabler:info-circle',
  },
};

/**
 * Alert component with multiple variants
 *
 * @example
 * <Alert variant="warning">
 *   Estás viendo una versión de prueba con datos ficticios.
 * </Alert>
 *
 * <Alert variant="success">
 *   Operación completada exitosamente.
 * </Alert>
 *
 * <Alert variant="danger">
 *   Ha ocurrido un error.
 * </Alert>
 */
const Alert: React.FC<AlertProps> = ({
  children,
  variant = 'default',
  className = '',
  showIcon = false,
}) => {
  const styles = variantStyles[variant];

  return (
    <div
      className={`p-4 border rounded-lg ${styles.container} ${className}`}
    >
      <div className={`${styles.text} text-sm font-medium ${showIcon ? 'flex items-start gap-2' : ''}`}>
        {showIcon && <Icon icon={styles.icon} width={18} className='mt-0.5 shrink-0' />}
        {showIcon ? <div className='min-w-0 flex-1'>{children}</div> : children}
      </div>
    </div>
  );
};

export default Alert;
